import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Plus, Check, Share2, Play, Pause, Volume2, VolumeX, Maximize, Minimize, RotateCcw, RotateCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { myListApi, watchProgressApi, filmsApi, adsApi, analyticsApi, liveAudienceApi } from '../lib/api';
import { toTitleCase } from '../lib/utils';
import { toast } from 'sonner';
import { AdPlayer } from './AdComponents';

const formatTime = (s) => {
  if (!s || isNaN(s) || !isFinite(s)) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const VideoPlayer = ({ film, showAds = false, hasEpisodeBar = false, compact = false, onVideoEnded, onTimeProgress, onBack, analyticsId }) => {
  const [inMyList, setInMyList] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [showCenterIcon, setShowCenterIcon] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Ad state
  const [adState, setAdState] = useState({
    showPreRoll: showAds,
    showMidRoll: false,
    preRollDuration: 5,
    midRollInterval: 600,
    skipAfter: 5
  });
  const [adConfig, setAdConfig] = useState(null);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimerRef = useRef(null);
  const progressSaveRef = useRef(null);
  const watchTimeRef = useRef(null);
  const lastRecordedTimeRef = useRef(0);
  const centerIconTimerRef = useRef(null);
  const midRollTriggeredRef = useRef(false);
  const progressBarRef = useRef(null);

  // ── Sync muted state to video element via ref (bypasses React muted prop bug) ──
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
  }, [isMuted]);

  // ── Handle autoplay policy: try unmuted, fallback to muted ──
  useEffect(() => {
    const v = videoRef.current;
    if (!v || adState.showPreRoll) return;
    // Try to play unmuted; if blocked by browser policy, mute and retry
    const tryPlay = () => {
      v.muted = false;
      const p = v.play();
      if (p && p.catch) {
        p.catch(() => {
          // Autoplay with audio blocked — fall back to muted
          v.muted = true;
          setIsMuted(true);
          v.play().catch(() => {});
        });
      }
    };
    if (v.readyState >= 2) tryPlay();
    else v.addEventListener('canplay', tryPlay, { once: true });
  }, [adState.showPreRoll]);

  // ── Sync volume to video element via ref ──
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = volume;
  }, [volume]);

  // ── Ad config ──
  useEffect(() => {
    if (!showAds || compact) return;
    const fetchAdConfig = async () => {
      try {
        const config = await adsApi.getConfig();
        setAdConfig(config);
        setAdState(prev => ({
          ...prev,
          showPreRoll: showAds && config.pre_roll_enabled,
          preRollDuration: config.pre_roll_duration || 5,
          midRollInterval: config.mid_roll_interval || 600,
          skipAfter: config.pre_roll_skip_after || 5
        }));
      } catch (e) { /* silent */ }
    };
    fetchAdConfig();
  }, [showAds, compact]);

  // ── My List check ──
  useEffect(() => {
    if (!isAuthenticated || !film || compact) return;
    myListApi.check(film.film_id).then(r => setInMyList(r.in_list)).catch(() => {});
  }, [film, isAuthenticated, compact]);

  // ── Increment view (always for the film/series, not episode) ──
  useEffect(() => {
    if (film && !compact) {
      filmsApi.incrementView(film.film_id).catch(() => {});
    }
  }, [film, compact]);

  // ── Restore watch progress on load (runs ONCE per mount, not on every film prop change) ──
  const progressRestoredRef = useRef(false);
  useEffect(() => {
    if (!film || !isAuthenticated || compact || progressRestoredRef.current) return;
    progressRestoredRef.current = true;
    console.log('[VideoPlayer] restoreProgress running ONCE for', film.film_id);
    const restoreProgress = async () => {
      try {
        const data = await watchProgressApi.get(film.film_id);
        if (data && data.progress > 0) {
          const waitForVideo = () => {
            const v = videoRef.current;
            if (v && v.readyState >= 1 && isFinite(v.duration) && v.duration > 0) {
              // Only restore if not near the end (>5% remaining)
              if (data.progress < v.duration * 0.95) {
                v.currentTime = data.progress;
                setCurrentTime(data.progress);
              }
            } else {
              setTimeout(waitForVideo, 200);
            }
          };
          waitForVideo();
        }
      } catch { /* no saved progress */ }
    };
    restoreProgress();
  }, [film, isAuthenticated, compact]);

  // ── Save watch progress periodically ──
  const saveProgress = useCallback(() => {
    if (!film || !isAuthenticated || !videoRef.current || compact) return;
    const ct = videoRef.current.currentTime;
    const dur = videoRef.current.duration;
    if (ct > 0) {
      watchProgressApi.save(film.film_id, Math.floor(ct), Math.floor(dur) || 0).catch(() => {});
    }
  }, [film, isAuthenticated, compact]);

  useEffect(() => {
    if (!film || !isAuthenticated || compact) return;
    progressSaveRef.current = setInterval(saveProgress, 15000);
    return () => {
      clearInterval(progressSaveRef.current);
      saveProgress();
    };
  }, [film, isAuthenticated, saveProgress, compact]);

  // ── Record watch time for analytics (every 30s of actual playback) ──
  useEffect(() => {
    if (!film || !isAuthenticated || compact) return;
    const trackId = analyticsId || film.film_id;
    lastRecordedTimeRef.current = 0;
    const recordWatchTime = () => {
      const v = videoRef.current;
      if (!v || v.paused || v.ended) return;
      const ct = v.currentTime;
      const elapsed = ct - lastRecordedTimeRef.current;
      if (elapsed >= 30) {
        analyticsApi.recordWatchTime(trackId, Math.floor(elapsed)).catch(() => {});
        lastRecordedTimeRef.current = ct;
      }
    };
    watchTimeRef.current = setInterval(recordWatchTime, 10000);
    return () => {
      clearInterval(watchTimeRef.current);
      const v = videoRef.current;
      if (v && !v.paused) {
        const remaining = v.currentTime - lastRecordedTimeRef.current;
        if (remaining > 5) {
          analyticsApi.recordWatchTime(trackId, Math.floor(remaining)).catch(() => {});
        }
      }
    };
  }, [film, isAuthenticated, compact, analyticsId]);

  // ── Live audience heartbeat (every 15s while playing) ──
  useEffect(() => {
    if (!film || !isAuthenticated || compact) return;
    const isYouTube = !film.video_file && !!film.youtube_id;
    let heartbeatInterval;
    const sendHeartbeat = () => {
      if (isYouTube) {
        // YouTube embeds: always send heartbeat while on the watch page
        liveAudienceApi.heartbeat(film.film_id).catch(() => {});
      } else {
        const v = videoRef.current;
        if (v && !v.paused && !v.ended) {
          liveAudienceApi.heartbeat(film.film_id).catch(() => {});
        }
      }
    };
    sendHeartbeat();
    heartbeatInterval = setInterval(sendHeartbeat, 15000);
    return () => {
      clearInterval(heartbeatInterval);
      liveAudienceApi.disconnect(film.film_id).catch(() => {});
    };
  }, [film, isAuthenticated, compact]);

  // ── Pause main video when ads are active ──
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (adState.showPreRoll || adState.showMidRoll) {
      v.pause();
    }
  }, [adState.showPreRoll, adState.showMidRoll]);

  // ── Mid-roll ad trigger ──
  useEffect(() => {
    if (!showAds || compact || !adConfig?.mid_roll_enabled || midRollTriggeredRef.current) return;
    const check = setInterval(() => {
      if (videoRef.current && videoRef.current.currentTime >= adConfig.mid_roll_interval && !midRollTriggeredRef.current) {
        midRollTriggeredRef.current = true;
        videoRef.current.pause();
        setAdState(prev => ({ ...prev, showMidRoll: true }));
        clearInterval(check);
      }
    }, 1000);
    return () => clearInterval(check);
  }, [showAds, adConfig, compact]);

  const handlePreRollComplete = () => {
    setAdState(prev => ({ ...prev, showPreRoll: false }));
    // User interacted (skipped ad), so we can unmute safely
    setIsMuted(false);
    if (videoRef.current) videoRef.current.play().catch(() => {});
  };

  const handleMidRollComplete = () => {
    setAdState(prev => ({ ...prev, showMidRoll: false }));
    if (videoRef.current) videoRef.current.play().catch(() => {});
  };

  // ── Controls auto-hide ──
  const resetControlsTimer = useCallback(() => {
    setControlsVisible(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) {
        setControlsVisible(false);
      }
    }, 3000);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = () => resetControlsTimer();
    el.addEventListener('mousemove', onMove);
    el.addEventListener('touchstart', onMove);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('touchstart', onMove);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, [resetControlsTimer]);

  // ── Flash center icon ──
  const flashIcon = (type) => {
    setShowCenterIcon(type);
    if (centerIconTimerRef.current) clearTimeout(centerIconTimerRef.current);
    centerIconTimerRef.current = setTimeout(() => setShowCenterIcon(null), 600);
  };

  // ── Player actions ──
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      flashIcon('play');
    } else {
      v.pause();
      flashIcon('pause');
    }
    resetControlsTimer();
  };

  const skip = (seconds) => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration)) return;
    const newTime = Math.max(0, Math.min(v.duration, v.currentTime + seconds));
    v.currentTime = newTime;
    setCurrentTime(newTime);
    flashIcon(seconds > 0 ? 'fwd' : 'bwd');
    resetControlsTimer();
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (val === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    const vid = videoRef.current;
    if (!el) return;
    try {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
      if (fsEl) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      } else {
        // Push a dummy history entry so mobile back button exits fullscreen instead of navigating
        window.history.pushState({ fullscreen: true }, '');
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        else if (el.msRequestFullscreen) el.msRequestFullscreen();
        else if (vid && vid.webkitEnterFullscreen) vid.webkitEnterFullscreen();
      }
    } catch {
      try { if (vid && vid.webkitEnterFullscreen) vid.webkitEnterFullscreen(); } catch {}
    }
  };

  // Sync fullscreen state + clean up dummy history entry on exit
  useEffect(() => {
    const onChange = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setIsFullscreen(isFs);
      // If exiting fullscreen, remove the dummy history entry we pushed
      if (!isFs && window.history.state?.fullscreen) {
        window.history.back(); // pops the dummy entry silently
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('MSFullscreenChange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('MSFullscreenChange', onChange);
    };
  }, []);

  // Handle mobile back button during fullscreen — exit fullscreen instead of navigating
  useEffect(() => {
    const onPopState = (e) => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
      if (fsEl) {
        e.preventDefault();
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ── Progress bar seeking ──
  const getSeekPosition = (e, bar) => {
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const seekToPosition = (e) => {
    const bar = progressBarRef.current;
    const v = videoRef.current;
    if (!bar || !v || !isFinite(v.duration) || v.duration <= 0) return;
    const fraction = getSeekPosition(e, bar);
    const newTime = fraction * v.duration;
    v.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const onProgressMouseDown = (e) => {
    e.preventDefault();
    setIsSeeking(true);
    seekToPosition(e);
    const onMove = (ev) => seekToPosition(ev);
    const onUp = () => {
      setIsSeeking(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onProgressTouchStart = (e) => {
    e.preventDefault();
    setIsSeeking(true);
    seekToPosition(e);
    const onMove = (ev) => { ev.preventDefault(); seekToPosition(ev); };
    const onEnd = () => {
      setIsSeeking(false);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  };

  // ── Video event handlers ──
  const onTimeUpdate = () => {
    if (!isSeeking && videoRef.current) {
      const ct = videoRef.current.currentTime;
      const dur = videoRef.current.duration;
      setCurrentTime(ct);
      if (onTimeProgress && isFinite(dur) && dur > 0) {
        onTimeProgress(ct, dur);
      }
    }
  };

  const onDurationChange = () => {
    if (videoRef.current && isFinite(videoRef.current.duration) && videoRef.current.duration > 0) {
      setDuration(videoRef.current.duration);
    }
  };

  const onLoadedMetadata = () => {
    if (videoRef.current && isFinite(videoRef.current.duration) && videoRef.current.duration > 0) {
      setDuration(videoRef.current.duration);
    }
  };

  const onProgress = () => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };

  const onPlayPause = () => {
    if (videoRef.current) {
      setIsPlaying(!videoRef.current.paused);
      if (videoRef.current.paused) setControlsVisible(true);
      else resetControlsTimer();
    }
  };

  const onLoadedData = () => setIsLoading(false);
  const onWaiting = () => setIsLoading(true);
  const onCanPlay = () => setIsLoading(false);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      switch (e.key) {
        case ' ':
        case 'k': e.preventDefault(); togglePlay(); break;
        case 'ArrowRight': e.preventDefault(); skip(10); break;
        case 'ArrowLeft': e.preventDefault(); skip(-10); break;
        case 'ArrowUp': e.preventDefault(); {
          const newVol = Math.min(1, volume + 0.1);
          setVolume(newVol);
          if (newVol > 0) setIsMuted(false);
        } break;
        case 'ArrowDown': e.preventDefault(); {
          const newVol = Math.max(0, volume - 0.1);
          setVolume(newVol);
          if (newVol === 0) setIsMuted(true);
        } break;
        case 'f': e.preventDefault(); toggleFullscreen(); break;
        case 'm': e.preventDefault(); toggleMute(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ── Double-click to toggle fullscreen ──
  const handleDoubleClick = (e) => {
    if (e.target.closest('[data-controls]')) return;
    toggleFullscreen();
  };

  // ── Single-click to toggle play/pause ──
  const handleClick = (e) => {
    if (e.target.closest('[data-controls]')) return;
    togglePlay();
  };

  const handleAddToList = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try {
      if (inMyList) {
        await myListApi.remove(film.film_id);
        setInMyList(false);
        toast.success('Removed from My List');
      } else {
        await myListApi.add(film.film_id);
        setInMyList(true);
        toast.success('Added to My List');
      }
    } catch { toast.error('Failed to update list'); }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (!film) return null;

  const API_BASE = window.location.origin || '';
  const platformVideoUrl = film.video_file
    ? (film.video_file.startsWith('http') ? film.video_file : `${API_BASE}${film.video_file}`)
    : null;
  const youtubeUrl = !platformVideoUrl && film.youtube_id
    ? `https://www.youtube.com/embed/${film.youtube_id}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${window.location.origin}`
    : null;

  const progress = (duration > 0 && isFinite(duration)) ? (currentTime / duration) * 100 : 0;
  const bufferedPct = (duration > 0 && isFinite(duration)) ? (buffered / duration) * 100 : 0;

  const containerClass = compact
    ? 'relative w-full aspect-video bg-black select-none rounded-lg overflow-hidden'
    : 'relative w-full h-screen bg-black select-none';

  return (
    <div
      ref={containerRef}
      className={containerClass}
      data-testid="video-player"
      style={{ cursor: controlsVisible ? 'default' : 'none' }}
      onClick={platformVideoUrl ? handleClick : undefined}
      onDoubleClick={platformVideoUrl ? handleDoubleClick : undefined}
    >
      {/* Pre-roll Ad */}
      {!compact && adState.showPreRoll && (
        <AdPlayer type="pre_roll" duration={adState.preRollDuration} skipAfter={adState.skipAfter}
          filmId={film.film_id} onComplete={handlePreRollComplete} onSkip={handlePreRollComplete} />
      )}

      {/* Mid-roll Ad */}
      {!compact && adState.showMidRoll && (
        <AdPlayer type="mid_roll" duration={adState.preRollDuration} skipAfter={adState.skipAfter}
          filmId={film.film_id} onComplete={handleMidRollComplete} onSkip={handleMidRollComplete} />
      )}

      {/* ── Video Element ── */}
      <div className="w-full h-full">
        {platformVideoUrl ? (
          <video
            ref={videoRef}
            src={platformVideoUrl}
            autoPlay={!adState.showPreRoll}
            playsInline
            preload="metadata"
            className="w-full h-full object-contain"
            data-testid="platform-video"
            onTimeUpdate={onTimeUpdate}
            onDurationChange={onDurationChange}
            onLoadedMetadata={onLoadedMetadata}
            onProgress={onProgress}
            onPlay={onPlayPause}
            onPause={onPlayPause}
            onLoadedData={onLoadedData}
            onWaiting={onWaiting}
            onCanPlay={onCanPlay}
            onEnded={() => { console.log('[VideoPlayer] onEnded fired'); setIsPlaying(false); setControlsVisible(true); if (onVideoEnded) onVideoEnded(); }}
          />
        ) : youtubeUrl ? (
          <iframe
            src={youtubeUrl}
            title={film.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="youtube-iframe"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <p>Video not available</p>
          </div>
        )}
      </div>

      {/* ── Click to unmute banner — always visible when muted ── */}
      {isMuted && platformVideoUrl && !adState.showPreRoll && !adState.showMidRoll && (
        <button
          onClick={(e) => { e.stopPropagation(); setIsMuted(false); }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 flex items-center gap-3 px-6 py-3 bg-black/70 hover:bg-black/90 backdrop-blur-sm rounded-full text-white transition-all cursor-pointer"
          data-testid="unmute-btn"
          data-controls="true"
        >
          <VolumeX className="w-5 h-5" />
          <span className="text-sm font-medium">Tap to unmute</span>
        </button>
      )}

      {/* ── Center flash icon ── */}
      {showCenterIcon && platformVideoUrl && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className="w-20 h-20 rounded-full bg-black/50 flex items-center justify-center animate-ping-once">
            {showCenterIcon === 'play' && <Play className="w-10 h-10 text-white fill-white" />}
            {showCenterIcon === 'pause' && <Pause className="w-10 h-10 text-white fill-white" />}
            {showCenterIcon === 'fwd' && <RotateCw className="w-10 h-10 text-white" />}
            {showCenterIcon === 'bwd' && <RotateCcw className="w-10 h-10 text-white" />}
          </div>
        </div>
      )}

      {/* ── Loading spinner ── */}
      {isLoading && platformVideoUrl && !adState.showPreRoll && !adState.showMidRoll && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* ── Controls overlay (platform video only) ── */}
      {platformVideoUrl && (
        <div
          className={`absolute inset-0 z-30 transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          {/* Top gradient + bar */}
          <div className={`absolute top-0 left-0 right-0 ${compact ? 'p-3' : 'p-4 md:p-6'} bg-gradient-to-b from-black/80 via-black/40 to-transparent`} data-controls="true">
            <div className="flex items-center justify-between">
              {!compact && (
                <button onClick={() => onBack ? onBack() : navigate(-1)}
                  className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
                  data-testid="back-button">
                  <ArrowLeft className="w-5 h-5" />
                  <span className="hidden md:inline text-sm font-medium">Back</span>
                </button>
              )}

              <h2 className={`${compact ? '' : 'absolute left-1/2 -translate-x-1/2'} text-sm ${compact ? '' : 'md:text-base'} font-medium text-white/80 max-w-[50%] truncate ${compact ? 'block' : 'hidden md:block'}`}
                data-testid="player-title">
                {toTitleCase(film.title)}
              </h2>

              {!compact && (
                <div className="flex items-center gap-2">
                  <button onClick={handleAddToList}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-sm"
                    data-testid="player-mylist-button">
                    {inMyList ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    <span className="hidden md:inline">{inMyList ? 'Listed' : 'My List'}</span>
                  </button>
                  <button onClick={handleShare}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    data-testid="share-button">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bottom controls */}
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent ${compact ? 'pt-8 px-3 pb-3' : `pt-16 px-4 md:px-6 ${hasEpisodeBar ? 'pb-16' : 'pb-4'}`}`} data-controls="true">
            {/* Progress bar */}
            <div
              ref={progressBarRef}
              className="group relative w-full h-5 flex items-center cursor-pointer mb-2"
              onMouseDown={onProgressMouseDown}
              onTouchStart={onProgressTouchStart}
              data-testid="progress-bar"
            >
              <div className="absolute left-0 right-0 h-1 group-hover:h-2 bg-white/20 rounded-full transition-all">
                {/* Buffered */}
                <div className="absolute inset-y-0 left-0 bg-white/30 rounded-full" style={{ width: `${Math.min(bufferedPct, 100)}%` }} />
                {/* Played - purple matching Sign In button #8B5CF6 */}
                <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-75" style={{ width: `${Math.min(progress, 100)}%`, backgroundColor: '#8B5CF6' }} />
              </div>
              {/* Scrubber knob */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${Math.min(progress, 100)}% - 8px)`, backgroundColor: '#8B5CF6' }}
                data-testid="scrubber-knob"
              />
            </div>

            {/* Control buttons row */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Play / Pause */}
              <button onClick={togglePlay} className="text-white hover:text-white/80 transition-colors" data-testid="play-pause-btn">
                {isPlaying
                  ? <Pause className={`${compact ? 'w-5 h-5' : 'w-7 h-7'} fill-white`} />
                  : <Play className={`${compact ? 'w-5 h-5' : 'w-7 h-7'} fill-white`} />}
              </button>

              {/* Skip backward */}
              <button onClick={() => skip(-10)} className="text-white/80 hover:text-white transition-colors" data-testid="skip-backward-btn">
                <div className={`relative ${compact ? 'w-5 h-5' : 'w-7 h-7'} flex items-center justify-center`}>
                  <RotateCcw className={compact ? 'w-4 h-4' : 'w-6 h-6'} />
                  <span className={`absolute ${compact ? 'text-[6px]' : 'text-[8px]'} font-bold mt-0.5`}>10</span>
                </div>
              </button>

              {/* Skip forward */}
              <button onClick={() => skip(10)} className="text-white/80 hover:text-white transition-colors" data-testid="skip-forward-btn">
                <div className={`relative ${compact ? 'w-5 h-5' : 'w-7 h-7'} flex items-center justify-center`}>
                  <RotateCw className={compact ? 'w-4 h-4' : 'w-6 h-6'} />
                  <span className={`absolute ${compact ? 'text-[6px]' : 'text-[8px]'} font-bold mt-0.5`}>10</span>
                </div>
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2">
                <button onClick={toggleMute} className="text-white/80 hover:text-white transition-colors" data-testid="mute-btn">
                  {isMuted || volume === 0 ? <VolumeX className={compact ? 'w-4 h-4' : 'w-5 h-5'} /> : <Volume2 className={compact ? 'w-4 h-4' : 'w-5 h-5'} />}
                </button>
                <input
                  type="range"
                  min="0" max="1" step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 cursor-pointer appearance-none bg-white/30 rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                  style={{
                    '--thumb-color': '#8B5CF6',
                    background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.3) 100%)`
                  }}
                  data-testid="volume-slider"
                />
              </div>

              {/* Time */}
              <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-white/70 font-mono ml-1`} data-testid="time-display">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Title on mobile */}
              {!compact && <span className="text-xs text-white/60 truncate max-w-[30%] md:hidden">{toTitleCase(film.title)}</span>}

              {/* Fullscreen */}
              {!compact && (
                <button onClick={toggleFullscreen} className="text-white/80 hover:text-white transition-colors" data-testid="fullscreen-btn">
                  {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── YouTube / fallback overlay (simplified) ── */}
      {!platformVideoUrl && (
        <div className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute top-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-b from-black/80 to-transparent pointer-events-auto">
            <div className="flex items-center justify-between">
              {!compact && (
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-[#8B5CF6] transition-colors" data-testid="back-button">
                  <ArrowLeft className="w-6 h-6" />
                  <span className="hidden md:inline">Back</span>
                </button>
              )}
              {!compact && (
                <div className="flex items-center gap-4">
                  <button onClick={handleAddToList} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors" data-testid="player-mylist-button">
                    {inMyList ? <><Check className="w-5 h-5" /><span className="hidden md:inline">In My List</span></> : <><Plus className="w-5 h-5" /><span className="hidden md:inline">My List</span></>}
                  </button>
                  <button onClick={handleShare} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors" data-testid="share-button">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {!compact && (
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/80 to-transparent pointer-events-auto">
              <h1 className="text-xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="player-title">{toTitleCase(film.title)}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-300">
                <span className="text-[#00FF9D] font-semibold">{film.avg_rating?.toFixed(1) || '0.0'} ★</span>
                <span>{film.year}</span>
                <span>{film.creator}</span>
              </div>
            </div>
          )}
        </div>
      )}


    </div>
  );
};
